import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// The other animation studies are preserved in commit c59c088b. Filigrana is
// the chosen version for the Venezuela landing.
const DURATION = 5.6;
const MODEL_URL = '/assets/models/yare-mask.optimized.glb';

function createSurfaceMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: { uProgress: { value: 0 }, uTime: { value: 0 } },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vView;
      varying vec3 vWorld;
      void main() {
        vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
        vNormal = normalize(normalMatrix * normal);
        vView = normalize(-viewPosition.xyz);
        vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * viewPosition;
      }`,
    fragmentShader: `
      uniform float uProgress;
      uniform float uTime;
      varying vec3 vNormal;
      varying vec3 vView;
      varying vec3 vWorld;
      void main() {
        float radial = length(vec2(vWorld.x * 0.72, vWorld.y + 0.17));
        float noise = fract(sin(dot(floor(vWorld * 21.0), vec3(127.1, 311.7, 74.7))) * 43758.5453);
        float threshold = radial * 0.72 + noise * 0.035;
        if (threshold > uProgress) discard;
        vec3 n = normalize(vNormal);
        float rim = pow(1.0 - abs(dot(n, normalize(vView))), 2.2);
        float wave = exp(-pow((threshold - uProgress) * 24.0, 2.0));
        float resonance = pow(0.5 + 0.5 * sin(radial * 34.0 - uTime * 7.0), 8.0) * (1.0 - uProgress);
        vec3 color = vec3(0.07, 0.085, 0.008) + rim * vec3(0.55, 0.66, 0.04) + (wave + resonance) * vec3(0.25, 0.33, 0.01);
        float alpha = 0.12 + rim * 0.43 + wave * 0.25;
        float edge = 1.0 - smoothstep(0.0, 0.055, uProgress - threshold);
        color += edge * vec3(0.45, 0.5, 0.01);
        gl_FragColor = vec4(color, alpha);
      }`,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function createFiligreeLines(mesh: THREE.Mesh) {
  const geometry = new THREE.EdgesGeometry(mesh.geometry, 14);
  const material = new THREE.ShaderMaterial({
    uniforms: { uProgress: { value: 0 } },
    vertexShader: `
      varying vec3 vWorld;
      void main() {
        vec4 world = modelMatrix * vec4(position, 1.0);
        vWorld = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }`,
    fragmentShader: `
      uniform float uProgress;
      varying vec3 vWorld;
      void main() {
        float reveal = length(vec2(vWorld.x * 0.78, vWorld.y + 0.16)) * 0.65;
        if (reveal > uProgress) discard;
        gl_FragColor = vec4(0.9, 1.0, 0.09, 0.42);
      }`,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  });
  const lines = new THREE.LineSegments(geometry, material);
  lines.renderOrder = 1;
  mesh.add(lines);
  return lines;
}

export function YareMask() {
  const mountRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.7));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.45;
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 30);
    const modelGroup = new THREE.Group();
    scene.add(modelGroup);
    const clock = new THREE.Clock();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let model: THREE.Object3D | null = null;
    let surfaceMaterial: THREE.ShaderMaterial | null = null;
    let lines: THREE.LineSegments | null = null;
    let startedAt = 0;
    let frame = 0;
    let disposed = false;
    let paused = false;
    let inView = true;

    const resize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      if (!width || !height) return;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.position.set(0, 0.04, width < 640 ? 7.8 : 5.7);
      camera.lookAt(0, -0.02, 0);
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    resize();

    const render = () => {
      frame = window.requestAnimationFrame(render);
      if (paused) return;
      const elapsed = clock.getElapsedTime();
      if (model) {
        const t = reducedMotion ? 1 : Math.min(1, (elapsed - startedAt) / DURATION);
        const eased = t * t * (3 - 2 * t);
        model.visible = true;
        if (surfaceMaterial) {
          surfaceMaterial.uniforms.uProgress.value = eased;
          surfaceMaterial.uniforms.uTime.value = elapsed - startedAt;
        }
        if (lines) (lines.material as THREE.ShaderMaterial).uniforms.uProgress.value = eased;
        modelGroup.rotation.y = reducedMotion ? 0 : Math.sin(elapsed * 0.34) * 0.12 + (1 - eased) * 0.32;
        modelGroup.rotation.x = reducedMotion ? 0 : (1 - eased) * 0.08;
        modelGroup.scale.setScalar(0.78 + eased * 0.22);
      }
      renderer.render(scene, camera);
    };
    render();

    const draco = new DRACOLoader();
    draco.setDecoderPath('/draco/');
    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);
    loader.load(
      MODEL_URL,
      (gltf) => {
        if (disposed) return;
        model = gltf.scene;
        const center = new THREE.Box3().setFromObject(model).getCenter(new THREE.Vector3());
        model.position.sub(center);
        modelGroup.add(model);
        surfaceMaterial = createSurfaceMaterial();
        model.traverse((node) => {
          if (!(node instanceof THREE.Mesh)) return;
          if (!lines) lines = createFiligreeLines(node);
          const oldMaterials = Array.isArray(node.material) ? node.material : [node.material];
          for (const material of oldMaterials) material.dispose();
          node.material = surfaceMaterial!;
        });
        model.visible = false;
        startedAt = clock.getElapsedTime();
      },
      undefined,
      () => { if (!disposed) setFailed(true); },
    );

    const onVisibility = () => {
      paused = document.hidden || !inView;
      if (!paused) clock.getDelta();
    };
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      onVisibility();
    }, { threshold: 0.01 });
    visibilityObserver.observe(mount);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      visibilityObserver.disconnect();
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      model?.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        node.geometry.dispose();
        const materials = Array.isArray(node.material) ? node.material : [node.material];
        for (const material of materials) material.dispose();
      });
      lines?.geometry.dispose();
      (lines?.material as THREE.Material | undefined)?.dispose();
      draco.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div className="absolute inset-0" aria-label="Máscara del Diablo de Yare en 3D">
      <div ref={mountRef} className="absolute inset-0" />
      {failed && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center font-mono text-xs uppercase tracking-[0.2em] text-primary/70">
          No se pudo cargar la máscara
        </div>
      )}
    </div>
  );
}
